const COMMON_FRACTIONS = [
  [1 / 12, "1/12"],
  [1 / 10, "1/10"],
  [1 / 9, "1/9"],
  [1 / 8, "1/8"],
  [1 / 6, "1/6"],
  [1 / 5, "1/5"],
  [1 / 4, "1/4"],
  [1 / 3, "1/3"],
  [3 / 8, "3/8"],
  [1 / 2, "1/2"],
  [5 / 8, "5/8"],
  [2 / 3, "2/3"],
  [3 / 4, "3/4"],
  [7 / 8, "7/8"],
];

const VALID_SLICE_FRACTIONS = [1 / 12, 1 / 10, 1 / 9, 1 / 8, 1 / 6, 1 / 5, 1 / 4, 1 / 3, 1 / 2, 1];
const VALID_TSP = [1 / 8, 1 / 4, 1 / 2, 3 / 4, 1, 2];
const VALID_TBSP = [1, 4 / 3, 3 / 2, 5 / 3, 2, 3];
const CUP_FRACTIONS = [0, 1 / 4, 1 / 3, 1 / 2, 2 / 3, 3 / 4, 1];

const CONVERSION_TABLE = {
  "drop": 0.05,
  "g": 1,
  "mL": 1,
  "oz": 28,
  "fl oz": 30,
  "tsp": 5,
  "tbsp": 15,
  "cup": 240,
};

export const amountFromUnit = (amount, unit) => amount * CONVERSION_TABLE[unit];

export const formatNumber = (n, commonFractions = true) => {
  const whole = Math.floor(n);
  const frac = n - whole;

  if (whole > 0 && frac < 0.01) return String(whole);

  if (commonFractions) {
    for (const [val, str] of COMMON_FRACTIONS) {
      if (Math.abs(frac - val) < 0.01) {
        return whole > 0 ? `${whole} ${str}` : str;
      }
    }
  }

  const rounded = Math.round(n * 100) / 100;
  return rounded === Math.floor(rounded) ? String(Math.floor(rounded)) : String(rounded);
};

const NO_PLURAL = new Set(["tsp", "tbsp", "oz", "ml", "g", "mg", "mcg"]);

export const pluralize = (text, n) => {
  if (NO_PLURAL.has(text)) return text;

  const suffix = /(?:s|sh|ch|x|z)$/i.test(text) ? "es" : "s";
  return n <= 1 ? text : `${text}${suffix}`;
};

const roundToNearest = (value, increments) => {
  if (value <= increments[0]) return increments[0];
  if (value >= increments[increments.length - 1]) return increments[increments.length - 1];

  for (let i = 0; i < increments.length - 1; i++) {
    if (value >= increments[i] && value <= increments[i + 1]) {
      const mid = (increments[i] + increments[i + 1]) / 2;
      return value >= mid ? increments[i + 1] : increments[i];
    }
  }

  return increments[increments.length - 1];
};

const roundCups = (cups) => {
  if (cups <= 1 / 4) return 1 / 4;

  const whole = Math.floor(cups);
  const frac = cups - whole;

  for (let i = 0; i < CUP_FRACTIONS.length - 1; i++) {
    if (frac >= CUP_FRACTIONS[i] && frac <= CUP_FRACTIONS[i + 1]) {
      const mid = (CUP_FRACTIONS[i] + CUP_FRACTIONS[i + 1]) / 2;
      const rounded = frac >= mid ? CUP_FRACTIONS[i + 1] : CUP_FRACTIONS[i];
      if (rounded >= 1) return whole + 1;
      return rounded === 0 ? whole || 1 / 4 : whole + rounded;
    }
  }

  return cups;
};

const roundHousehold = (value, unit) => {
  switch (unit) {
    case "tsp":
      return roundToNearest(value, VALID_TSP);
    case "tbsp":
      return roundToNearest(value, VALID_TBSP);
    case "cup":
      return roundCups(value);
    case "fl oz":
    case "oz":
      return Math.round(value * 2) / 2 || 0.5;
    default:
      return roundToNearest(value, VALID_SLICE_FRACTIONS);
  }
};

// https://www.ecfr.gov/current/title-21/part-101/section-101.9#p-101.9(b)(5)
const formatHousehold = (value, unit) => {
  const rounded = roundHousehold(value, unit);
  return unit === "fl oz" || unit === "oz" ? String(rounded) : formatNumber(rounded);
};

const cleanUnit = (unit) => unit.replace(/(.*)\s+\(.*\)/, "$1");

const noRacc = { raccUnits: null, raccPercentage: null };

const computeCustomServingSize = ({
  inputType,
  amount,
  labelUnit,
  pieceUnit,
  showPieceUnit,
  referenceAmount,
  referenceValue,
  raccUnits,
}) => {
  let actualLabelUnit = showPieceUnit ? pieceUnit : labelUnit;
  if (inputType === "8_square_inches") {
    const rounded = roundToNearest(8 / amount, VALID_SLICE_FRACTIONS);
    return {
      ...noRacc,
      serving: `${formatNumber(rounded)} ${pluralize(actualLabelUnit, rounded)}`,
    };
  } else if (inputType === "amount_with_label_unit") {
    return {
      ...noRacc,
      serving: `${amount} ${pluralize(actualLabelUnit, amount)}`,
    };
  } else if (inputType === "amount_to_make") {
    return {
      ...noRacc,
      serving: `${formatNumber(amount)} ${pluralize(actualLabelUnit, amount)}`,
    };
  } else if (inputType === "spray") {
    return {
      ...noRacc,
      serving: `About ${amount} seconds spray`,
    };
  } else if (inputType === "compound_format") {
    const servingWeight = referenceValue;
    const { primary, secondary } = referenceAmount.compound_units;
    actualLabelUnit = showPieceUnit ? pieceUnit : secondary;
    const fixedAmount = formatHousehold(referenceValue / CONVERSION_TABLE[primary], primary);
    const fixedValue = `${fixedAmount} ${primary}`;
    const secondaryAmount = CONVERSION_TABLE[secondary]
      ? formatHousehold(raccUnits, secondary)
      : raccUnits >= 1
        ? formatNumber(Math.round(raccUnits))
        : formatNumber(roundToNearest(raccUnits, VALID_SLICE_FRACTIONS));
    const variableValue = `${secondaryAmount} ${pluralize(actualLabelUnit, raccUnits)}`;

    return {
      servingWeight,
      serving: fixedValue,
      visualUnit: variableValue,
    };
  }
};

export const valueWithModifiers = (value, modifiers) => {
  let actualValue = value;
  modifiers
    .filter(({ type }) => type === "value")
    .forEach((modifier) => {
      actualValue += modifier.value;
    });

  return actualValue;
};

export const allPossibleReferenceValues = (category) => {
  return category.amounts.flatMap(({ value, unit, options }) => {
    const modifiedValues = (options ?? []).map((optName) => {
      const optionModifiers = (category.options ?? []).reduce((arr, opt) => {
        const { name, type } = opt;
        if (name !== optName) return arr;

        let possibleModifiers;
        if (type === "checkbox") {
          possibleModifiers = opt.modifiers;
        } else {
          possibleModifiers = opt.values.flatMap((v) => v.modifiers);
        }
        arr.push(...possibleModifiers);

        return arr;
      }, []);
      return { value: valueWithModifiers(value, optionModifiers), unit };
    });

    return [{ value, unit }, ...modifiedValues];
  });
};

export const GROUP_HUES = {
  "Bakery Products": 35,
  "Beverages": 208,
  "Cereals and Other Grain Products": 47,
  "Dairy Products and Substitutes": 193,
  "Desserts": 345,
  "Dessert Toppings and Fillings": 312,
  "Egg and Egg Substitutes": 52,
  "Fats and Oils": 70,
  "Fish, Shellfish, Game Meats, and Meat or Poultry Substitutes": 178,
  "Fruits and Fruit Juices": 22,
  "Legumes": 85,
  "Mixed Dishes": 270,
  "Nuts and Seeds": 28,
  "Potatoes and Sweet Potatoes/Yams": 18,
  "Salads": 115,
  "Sauces, Dips, Gravies, and Condiments": 4,
  "Snacks": 14,
  "Soups": 40,
  "Sugars and Sweets": 328,
  "Vegetables": 148,
};

export const tagStyle = (group) => {
  const hue = GROUP_HUES[group] ?? 220;
  return { background: `hsl(${hue}, 65%, 87%)`, color: `hsl(${hue}, 80%, 22%)` };
};

export const groupBorderStyle = (group) => {
  const hue = GROUP_HUES[group] ?? 220;
  return { borderLeftColor: `hsl(${hue}, 65%, 55%)` };
};

export const computeConnectedFieldValue = ({
  servingSize,
  inputType,
  amountToMake,
  referenceAmount,
  perPackageWeight,
  yieldMethod,
}) => {
  const servingWeight =
    inputType === "amount_to_make" ? amountToMake : (servingSize?.servingWeight ?? referenceAmount);

  if (!servingWeight) return null;

  if (yieldMethod === "package_size_and_count") {
    return formatNumber(perPackageWeight / servingWeight, false);
  } else if (yieldMethod === "ingredient_weight") {
    return servingWeight;
  }

  return null;
};

export const effectiveReferenceWeight = ({ referenceValue, labelUnit, amount }) => {
  // e.g. smoked fish
  // fixed_labels: [{ label: "2 oz", amount: 2, unit: "oz", visual_unit: true }]
  // Should always be 2 oz (56g).
  if (labelUnit?.visual_unit) {
    return amountFromUnit(labelUnit.amount, labelUnit.unit);
  }

  // e.g. ice cream bulk (fixed_labels) or pieces (label_units with racc_fraction)
  // The RACC is a fraction of a cup, so the weight depends on the product density.
  // `amount` here is the weight per cup/racc_unit entered by the user.
  if (labelUnit?.amount || labelUnit?.racc_fraction) {
    const fraction = labelUnit.amount || labelUnit.racc_fraction;
    return amount ? Number(amount) * fraction : null;
  }

  // Static reference values
  return referenceValue;
};

const computeWarnings = ({ referenceAmount, amount, raccUnits, labelUnit, category }) => {
  const warnings = [];

  if (referenceAmount.ruleset === 3 && raccUnits && !referenceAmount.fixed_labels?.length) {
    const unit = cleanUnit(labelUnit?.unit || labelUnit);
    const householdBounds = {
      tsp: { min: VALID_TSP[0], max: VALID_TSP[VALID_TSP.length - 1] },
      tbsp: { min: VALID_TBSP[0], max: VALID_TBSP[VALID_TBSP.length - 1] },
      cup: { min: CUP_FRACTIONS[1] },
    };
    const bounds = householdBounds[unit];
    if (bounds?.max && raccUnits > bounds.max) {
      warnings.push(`Suggested serving exceeds the maximum allowed for ${unit} (${bounds.max}).`);
    } else if (bounds?.min && raccUnits < bounds.min) {
      warnings.push(
        `Suggested serving is below the minimum allowed for ${unit} (${formatNumber(bounds.min)}).`
      );
    }
  }

  const numAmount = Number(amount);
  if (category?.max_amount && numAmount > category.max_amount) {
    warnings.push(
      `Amount exceeds the expected maximum of ${category.max_amount} for this category.`
    );
  }
  if (category?.min_amount && numAmount < category.min_amount) {
    warnings.push(
      `Amount is below the expected minimum of ${category.min_amount} for this category.`
    );
  }

  return warnings.length ? { warnings } : {};
};

export const computeServingSize = ({
  referenceAmount,
  amount,
  inputType,
  labelUnit,
  pieceUnit,
  showPieceUnit,
  visualUnit = "",
  perPackageWeight,
  singleServingUnit,
  category,
  modifiers = [],
}) => {
  if (!referenceAmount) return null;

  const referenceValue = valueWithModifiers(referenceAmount.value, modifiers);
  const referenceWeight = effectiveReferenceWeight({ referenceValue, labelUnit, amount });

  // 21 CFR 101.9(b)(6) - Single-serving containers
  if (perPackageWeight && referenceWeight && perPackageWeight < 2 * referenceWeight) {
    const unit = singleServingUnit || "package";
    const raccPercentage = Math.round((perPackageWeight / referenceWeight) * 100);
    const raccUnits = referenceWeight / perPackageWeight;

    return {
      serving: `1 ${unit}`,
      servingWeight: perPackageWeight,
      singleServing: true,
      raccPercentage,
      raccUnits,
    };
  }

  const hasFixedLabels = referenceAmount.fixed_labels?.length;
  const needsAmount = !hasFixedLabels || (labelUnit?.amount && !labelUnit?.visual_unit);
  if (needsAmount && (!amount || Number(amount) === 0 || isNaN(Number(amount)))) return null;

  const raccUnits = referenceValue / amount;
  const weightRaccRatio = amount / referenceValue;
  const raccPercentage = Math.round(weightRaccRatio * 100);

  const racc = hasFixedLabels ? {} : { raccUnits, raccPercentage };

  if (inputType) {
    return {
      ...racc,
      ...computeCustomServingSize({
        inputType,
        amount,
        labelUnit,
        pieceUnit,
        showPieceUnit,
        referenceAmount,
        referenceValue,
        raccUnits,
      }),
    };
  }

  let serving, numericValue, value, hint, servingWeight, servingVisualUnit;
  if (raccUnits) {
    switch (referenceAmount.ruleset) {
      case 1: // 21 CFR 101.9(b)(2)(i)
        if (weightRaccRatio <= 0.5) {
          // 21 CFR 101.9(b)(2)(i)(A)
          numericValue = Math.round(raccUnits);
        } else if (weightRaccRatio < 0.67) {
          // 21 CFR 101.9(b)(2)(i)(B)
          numericValue = 1;
          hint = "You may declare one unit or two units as the serving size";
        } else if (weightRaccRatio < 2) {
          // 21 CFR 101.9(b)(2)(i)(C)
          numericValue = 1;
        } else if (weightRaccRatio <= 3) {
          // 21 CFR 101.9(b)(2)(i)(D)
          numericValue = roundToNearest(raccUnits, VALID_SLICE_FRACTIONS);
          hint = "Dual column label required for this serving size";
        } else {
          numericValue = roundToNearest(raccUnits, VALID_SLICE_FRACTIONS);
          hint =
            "The FDA doesn't describe how to handle pieces with over 300% of the reference amount.";
        }
        break;
      case 2: {
        // 21 CFR 101.9(b)(2)(ii)
        numericValue = roundToNearest(raccUnits, VALID_SLICE_FRACTIONS);
        break;
      }
      case 3: {
        // 21 CFR 101.9(b)(2)(iii)
        const unit = cleanUnit(labelUnit?.unit || labelUnit);
        numericValue = roundHousehold(raccUnits, unit);
        break;
      }
    }

    value =
      referenceAmount.ruleset === 3
        ? formatHousehold(numericValue, cleanUnit(labelUnit?.unit || labelUnit))
        : formatNumber(numericValue);

    servingWeight = numericValue * amount;
  }

  const format = modifiers.find(({ type }) => type === "format")?.value || referenceAmount.format;

  const actualLabelUnit = showPieceUnit ? pieceUnit : labelUnit?.unit || labelUnit;

  if (format) {
    serving = format
      .replace("##amount", amount)
      .replace("##unit", actualLabelUnit && pluralize(actualLabelUnit, amount));
  } else if (hasFixedLabels) {
    if (labelUnit?.visual_unit) {
      serving = labelUnit.label;
      servingVisualUnit = visualUnit;
      servingWeight = amountFromUnit(labelUnit.amount, labelUnit.unit);
    } else if (labelUnit?.amount) {
      serving = labelUnit.label;
      servingWeight = Number(amount) * labelUnit.amount;
    } else {
      serving = actualLabelUnit;
      servingWeight = referenceValue;
    }
  } else {
    if (!numericValue) return null;
    const servingUnit = actualLabelUnit && pluralize(actualLabelUnit, numericValue);

    // __ oz (__ g/visual unit of measure)
    if (referenceAmount.oz_visual) {
      const ozAmount = servingWeight / CONVERSION_TABLE["oz"];
      const ozFormatted = formatHousehold(ozAmount, "oz");
      serving = `${ozFormatted} oz`;
      servingVisualUnit = `${value} ${servingUnit}`;
    } else {
      serving = [value, servingUnit].join(" ");
      if (labelUnit?.visual_unit) {
        servingVisualUnit = visualUnit;
      }
    }
  }

  return {
    ...racc,
    ...computeWarnings({ referenceAmount, amount, raccUnits, labelUnit, category }),
    serving,
    servingWeight,
    visualUnit: servingVisualUnit,
    hint,
  };
};
